import { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { ReactComponent as TwoCalendarIcon } from "assets/morning/icon/TwoCalenderIcon.svg";

const formatDate = (date: Date | undefined) => {
    return date instanceof Date && !isNaN(date.getTime()) ? format(date, "yyyy. MM. dd") : "";
};

const parseDate = (input: string): Date | undefined => {
    const cleanedInput = input.replace(/\D/g, "");
    if (cleanedInput.length === 8) {
        const year = parseInt(cleanedInput.substring(0, 4), 10);
        const month = parseInt(cleanedInput.substring(4, 6), 10) - 1;
        const day = parseInt(cleanedInput.substring(6, 8), 10);
        const date = new Date(year, month, day);
        return isNaN(date.getTime()) ? undefined : date;
    }
    return undefined;
};

const formatInput = (input: string): string => {
    const cleanedInput = input.replace(/\D/g, "");
    let formatted = cleanedInput;

    if (cleanedInput.length > 4) {
        formatted = `${cleanedInput.substring(0, 4)}. ${cleanedInput.substring(4)}`;
    }
    if (cleanedInput.length > 6) {
        formatted = `${cleanedInput.substring(0, 4)}. ${cleanedInput.substring(4, 6)}. ${cleanedInput.substring(6)}`;
    }

    return formatted;
};

interface DayPickerInputProps {
    className?: string;
    initialDate?: Date;
    twoCalendar?: boolean;
    onDateChange?: (date: Date | undefined) => void;
    right?: boolean;
    disabled?: boolean;
    minDate?: Date;
    maxDate?: Date;
    placeholder?: string;
}
const DayPickerInput = ({ className, initialDate, twoCalendar, onDateChange, right, disabled, maxDate, minDate, placeholder = "기간" }: DayPickerInputProps) => {
    const previousDateRef = useRef<Date | undefined>(undefined);

    const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
    const [inputValue, setInputValue] = useState(formatDate(initialDate));
    const [isOpen, setIsOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const calendarRef = useRef<HTMLDivElement>(null);

    const handleDateChange = (date: Date | undefined) => {
        if (date) {
            const adjustedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()); // 타임존 보정
            if (minDate && adjustedDate < minDate) return;
            if (maxDate && adjustedDate > maxDate) return;

            setSelectedDate(adjustedDate);
            setInputValue(formatDate(adjustedDate));
            onDateChange?.(adjustedDate);
        } else {
            setSelectedDate(undefined);
            setInputValue("");
            onDateChange?.(undefined);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { selectionStart } = e.target;
        const rawValue = e.target.value;
        const formattedValue = formatInput(rawValue);

        setInputValue(formattedValue);
        const parsedDate = parseDate(rawValue);
        if (parsedDate) {
            setSelectedDate(parsedDate);
            onDateChange?.(parsedDate);
        } else {
            // 입력값을 지우거나 불완전하게 만들면 유효한 날짜가 없으므로 부모 상태도 초기화
            setSelectedDate(undefined);
            onDateChange?.(undefined);
        }

        requestAnimationFrame(() => {
            if (inputRef.current && selectionStart !== null) {
                let cursorPosition = selectionStart;
                const diff = formattedValue.length - rawValue.length;
                cursorPosition += diff > 0 ? diff : 0;
                inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
            }
        });
    };

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                calendarRef.current &&
                !calendarRef.current.contains(event.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (!initialDate) {
            setSelectedDate(undefined);
            setInputValue("");
            previousDateRef.current = undefined;
        } else if (previousDateRef.current?.getTime() !== initialDate.getTime()) {
            setSelectedDate(initialDate);
            setInputValue(formatDate(initialDate));
            previousDateRef.current = initialDate;
        }
    }, [initialDate]);

    return (
        <div className={`relative w-full flex items-center ${className}`} ref={calendarRef}>
            <input
                disabled={disabled}
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                placeholder={placeholder}
                maxLength={13}
                className="disabled:bg-white h-full z-10 focus:outline-none rounded w-full  placeholder:text-grayicon"
            />
            <button disabled={disabled} type="button" className="mt-[2px]" onClick={() => setIsOpen(!isOpen)}>
                <TwoCalendarIcon />
            </button>

            {isOpen && (
                <div className={`absolute z-10 bg-white shadow-lg py-3 pr-3 pl-4 border rounded ${right ? "left-0" : "right-0"} top-12`}>
                    <DayPicker
                        mode="single"
                        selected={selectedDate}
                        onSelect={date => handleDateChange(date as Date | undefined)}
                        locale={ko}
                        defaultMonth={selectedDate}
                        disabled={
                            [minDate ? { before: minDate } : undefined, maxDate ? { after: maxDate } : undefined].filter(Boolean) as any
                        }
                        formatters={{
                            formatCaption: month => format(month, "yyyy년 MM월", { locale: ko }),
                        }}
                        className="[&_.rdp-day]:px-0 [&_.rdp-day]:w-7 [&_.rdp-day]:h-7 "
                    />
                </div>
            )}
        </div>
    );
};

export default DayPickerInput;
